<template>
	<van-popup
		v-model:show="visible"
		position="bottom"
		round
		:style="{ height: '72vh' }"
		class="comment-drawer"
		@closed="handleClosed"
	>
		<div class="comment-drawer__inner">
			<header class="comment-drawer__header">
				<h2 class="comment-drawer__title">
					{{ t("square.detail.comments") }}
					<span>{{ commentTotal }}</span>
				</h2>
				<button type="button" class="comment-drawer__close" @click="visible = false">×</button>
			</header>

			<div class="comment-drawer__tabs">
				<button
					type="button"
					class="comment-drawer__tab"
					:class="{ 'comment-drawer__tab--active': commentTab === 'hot' }"
					@click="commentTab = 'hot'"
				>
					{{ t("square.detail.shorts.tabHot") }}
				</button>
				<button
					type="button"
					class="comment-drawer__tab"
					:class="{ 'comment-drawer__tab--active': commentTab === 'new' }"
					@click="commentTab = 'new'"
				>
					{{ t("square.detail.shorts.tabNew") }}
				</button>
			</div>

			<div class="comment-drawer__body">
				<ul v-if="commentList.length" class="comment-drawer__list">
					<li v-for="item in commentList" :key="item.id" class="comment-drawer__item">
						<img
							:src="item.user_info?.avatar"
							:alt="item.user_info?.nickname"
							class="comment-drawer__avatar"
						/>
						<div class="comment-drawer__content">
							<div class="comment-drawer__head">
								<span class="comment-drawer__user">{{ item.user_info?.nickname }}</span>
								<span class="comment-drawer__time">{{ item.format_time }}</span>
								<span v-if="isAuthorComment(item)" class="comment-drawer__author-tag">
									{{ t("square.detail.authorTag") }}
								</span>
							</div>
							<p class="comment-drawer__text">{{ item.reply }}</p>
							<div class="comment-drawer__actions">
								<button
									type="button"
									class="comment-drawer__zan"
									:disabled="isCommentZanLoading(item.id)"
									@click.stop="handleCommentZan(item)"
								>
									<img :src="item.is_zan ? zanAfterIcon : zanBeforeIcon" alt="" />
									<span>{{ item.zan || 0 }}</span>
								</button>
								<button
									type="button"
									class="comment-drawer__reply"
									@click.stop="toggleReplyInput(item)"
								>
									<img :src="pinlunIcon" alt="" />
								</button>
							</div>
							<div
								v-if="String(activeReplyId) === String(item.id)"
								class="comment-drawer__reply-input"
							>
								<input
									v-model="activeReplyDraft"
									type="text"
									:placeholder="
										t('square.detail.replyPlaceholder', {
											nickname: item.user_info?.nickname || '',
										})
									"
									maxlength="500"
									:disabled="replySubmitting"
									@keyup.enter="submitReplyComment(item)"
								/>
								<button
									type="button"
									class="comment-drawer__send-inline"
									:disabled="replySubmitting || !activeReplyDraft.trim()"
									@click="submitReplyComment(item)"
								>
									{{ replySubmitting ? t("square.detail.sending") : t("square.detail.send") }}
								</button>
							</div>
							<button
								v-if="item.reply_count > 0"
								type="button"
								class="comment-drawer__view-replies"
								:disabled="getReplyState(item.id).loading"
								@click="toggleViewReplies(item)"
							>
								<template v-if="getReplyState(item.id).loading">{{
									t("square.detail.loading")
								}}</template>
								<template v-else-if="getReplyState(item.id).expanded">{{
									t("square.detail.collapseReplies")
								}}</template>
								<template v-else
									>{{ t("square.detail.viewReplies", { count: item.reply_count }) }} &gt;</template
								>
							</button>
							<ul
								v-if="getReplyState(item.id).expanded && getReplyState(item.id).list.length"
								class="comment-drawer__reply-list"
							>
								<li
									v-for="reply in getReplyState(item.id).list"
									:key="reply.id"
									class="comment-drawer__reply-item"
								>
									<img
										:src="reply.user_info?.avatar"
										:alt="reply.user_info?.nickname"
										class="comment-drawer__reply-avatar"
									/>
									<div class="comment-drawer__reply-body">
										<div class="comment-drawer__head">
											<span class="comment-drawer__user">{{ reply.user_info?.nickname }}</span>
											<span class="comment-drawer__time">{{ reply.format_time }}</span>
										</div>
										<p class="comment-drawer__text">{{ reply.reply }}</p>
										<button
											type="button"
											class="comment-drawer__zan"
											:disabled="isCommentZanLoading(reply.id)"
											@click.stop="handleCommentZan(reply)"
										>
											<img :src="reply.is_zan ? zanAfterIcon : zanBeforeIcon" alt="" />
											<span>{{ reply.zan || 0 }}</span>
										</button>
									</div>
								</li>
								<li v-if="hasMoreReplies(item.id)" class="comment-drawer__load-more-wrap">
									<button
										type="button"
										class="comment-drawer__view-replies"
										:disabled="getReplyState(item.id).loading"
										@click="loadMoreReplies(item)"
									>
										{{
											getReplyState(item.id).loading
												? t("square.detail.loading")
												: t("square.detail.loadMoreReplies")
										}}
									</button>
								</li>
							</ul>
						</div>
					</li>
				</ul>
				<p v-else-if="!commentLoading" class="comment-drawer__empty">
					{{ t("square.detail.emptyComments") }}
				</p>
				<button
					v-if="hasMoreComments"
					type="button"
					class="comment-drawer__load-more"
					:disabled="commentLoading"
					@click="loadMoreComments"
				>
					{{ commentLoading ? t("square.detail.loading") : t("square.detail.loadMoreComments") }}
				</button>
			</div>

			<div class="comment-drawer__input-bar">
				<div class="comment-drawer__input-wrap">
					<img :src="writeIcon" alt="" class="comment-drawer__write-icon" aria-hidden="true" />
					<input
						v-model="mainCommentText"
						type="text"
						:placeholder="t('square.detail.commentPlaceholder')"
						maxlength="500"
						:disabled="commentSubmitting"
						@focus="onCommentInputFocus"
						@keyup.enter="submitMainComment"
					/>
					<button
						type="button"
						class="comment-drawer__send-btn"
						:class="{ 'comment-drawer__send-btn--active': mainCommentText.trim() }"
						:disabled="commentSubmitting || !mainCommentText.trim()"
						@click="submitMainComment"
					>
						<img :src="mainCommentText.trim() ? submit2 : submit1" alt="" />
					</button>
				</div>
			</div>
		</div>
	</van-popup>
</template>

<script setup>
import { computed, inject } from "vue";
import { useI18n } from "~/composables/useI18n";
import {
	buildDetailContentParams,
	resolveDetailMediaType,
} from "../../../composables/useSquareDetailTracking";
import zanAfterIcon from "../../../images/zan-after.png";
import zanBeforeIcon from "../../../images/zan-before.png";
import pinlunIcon from "../../../images/pinlun.png";
import moreIcon from "../../../images/more.png";
import writeIcon from "../../../images/write.svg";
import submit1 from "../../../images/submit1.png";
import submit2 from "../../../images/submit2.png";

const props = defineProps({
	show: {
		type: Boolean,
		default: false,
	},
	squareData: {
		type: Object,
		default: () => ({}),
	},
	commentTab: {
		type: String,
		default: "hot",
	},
	commentList: {
		type: Array,
		default: () => [],
	},
	commentLoading: {
		type: Boolean,
		default: false,
	},
	commentTotal: {
		type: Number,
		default: 0,
	},
	hasMoreComments: {
		type: Boolean,
		default: false,
	},
	mainCommentText: {
		type: String,
		default: "",
	},
	activeReplyId: {
		type: [String, Number],
		default: null,
	},
	activeReplyDraft: {
		type: String,
		default: "",
	},
	commentSubmitting: {
		type: Boolean,
		default: false,
	},
	replySubmitting: {
		type: Boolean,
		default: false,
	},
	getReplyState: {
		type: Function,
		default: () => ({}),
	},
	hasMoreReplies: {
		type: Function,
		default: () => false,
	},
	toggleViewReplies: {
		type: Function,
		default: () => {},
	},
	loadMoreReplies: {
		type: Function,
		default: () => {},
	},
	loadMoreComments: {
		type: Function,
		default: () => {},
	},
	toggleReplyInput: {
		type: Function,
		default: () => {},
	},
	submitMainComment: {
		type: Function,
		default: () => {},
	},
	submitReplyComment: {
		type: Function,
		default: () => {},
	},
	handleCommentZan: {
		type: Function,
		default: () => {},
	},
	isCommentZanLoading: {
		type: Function,
		default: () => false,
	},
});

const emit = defineEmits([
	"update:show",
	"update:commentTab",
	"update:mainCommentText",
	"update:activeReplyDraft",
]);

const { t } = useI18n();
const logDetailEvent = inject("logDetailEvent", null);

const onCommentInputFocus = () => {
	if (!logDetailEvent) return;
	logDetailEvent(
		"media_comment_input_focus",
		buildDetailContentParams(props.squareData, {
			media_type: resolveDetailMediaType(props.squareData),
		})
	);
};

const visible = computed({
	get: () => props.show,
	set: value => emit("update:show", value),
});

const commentTab = computed({
	get: () => props.commentTab,
	set: value => emit("update:commentTab", value),
});

const mainCommentText = computed({
	get: () => props.mainCommentText,
	set: value => emit("update:mainCommentText", value),
});

const activeReplyDraft = computed({
	get: () => props.activeReplyDraft,
	set: value => emit("update:activeReplyDraft", value),
});

const authorUid = computed(() => props.squareData?.uid || props.squareData?.publisher_info?.uid);

const isAuthorComment = comment => {
	if (!authorUid.value || !comment?.user_info?.uid) return false;
	return String(comment.user_info.uid) === String(authorUid.value);
};

const handleClosed = () => {
	emit("update:show", false);
};
</script>

<style lang="scss" scoped>
.comment-drawer {
	&__inner {
		display: flex;
		flex-direction: column;
		height: 100%;
		background: #ffffff;
	}

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 28px 30px 20px;
		flex-shrink: 0;
	}

	&__title {
		margin: 0;
		font-size: 36px;
		font-weight: 700;
		color: #333333;

		span {
			margin-left: 8px;
			font-size: 28px;
			font-weight: 400;
			color: #595959;
		}
	}

	&__close {
		border: none;
		background: transparent;
		font-size: 40px;
		line-height: 1;
		color: #333333;
		padding: 0 8px;
		cursor: pointer;
	}

	&__tabs {
		display: flex;
		gap: 40px;
		padding: 0 30px;
		border-bottom: 1px solid #f0f0f0;
		flex-shrink: 0;
	}

	&__tab {
		border: none;
		background: transparent;
		padding: 0 0 16px;
		font-size: 28px;
		color: #a0a0a0;
		cursor: pointer;
		position: relative;

		&--active {
			color: #333333;
			font-weight: 600;

			&::after {
				content: "";
				position: absolute;
				left: 0;
				right: 0;
				bottom: 0;
				height: 4px;
				background: #1f2329;
				border-radius: 2px;
			}
		}
	}

	&__body {
		flex: 1;
		overflow-y: auto;
		padding: 24px 30px;
		min-height: 0;
	}

	&__list {
		list-style: none;
		margin: 0;
		padding: 0;
	}

	&__item {
		display: flex;
		gap: 20px;
		margin-bottom: 36px;

		&:last-child {
			margin-bottom: 0;
		}
	}

	&__avatar {
		width: 48px;
		height: 48px;
		border-radius: 50%;
		object-fit: cover;
		flex-shrink: 0;
		background: #eeeeee;
	}

	&__content {
		flex: 1;
		min-width: 0;
	}

	&__head {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px;
		margin-bottom: 8px;
	}

	&__user {
		font-size: 24px;
		color: #585858;
		font-weight: 500;
	}

	&__time {
		font-size: 24px;
		color: #585858;
	}

	&__author-tag {
		padding: 2px 10px;
		border-radius: 4px;
		background: #1f2329;
		color: #ffffff;
		font-size: 20px;
		line-height: 1.4;
	}

	&__text {
		margin: 0 0 16px;
		font-size: 28px;
		color: #333333;
		line-height: 1.6;
		word-break: break-word;
	}

	&__actions {
		display: flex;
		align-items: center;
		gap: 32px;
	}

	&__zan,
	&__reply {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		border: none;
		background: transparent;
		padding: 0;
		cursor: pointer;
		font-size: 24px;
		color: #333333;

		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}

		img {
			width: 32px;
			height: 32px;
			object-fit: contain;
		}
	}

	&__more {
		margin-left: auto;
		border: none;
		background: transparent;
		padding: 0;
		cursor: pointer;

		img {
			width: 32px;
			height: 32px;
			object-fit: contain;
		}
	}

	&__reply-input {
		display: flex;
		align-items: center;
		gap: 16px;
		margin-top: 16px;

		input {
			flex: 1;
			min-width: 0;
			height: 64px;
			padding: 0 24px;
			border: none;
			border-radius: 32px;
			background: #f5f5f5;
			font-size: 26px;
			outline: none;
		}
	}

	&__send-inline {
		flex-shrink: 0;
		height: 56px;
		padding: 0 24px;
		border: none;
		border-radius: 28px;
		background: #f2af19;
		color: #ffffff;
		font-size: 24px;
		cursor: pointer;

		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}
	}

	&__view-replies {
		border: none;
		background: transparent;
		padding: 0;
		margin-top: 12px;
		font-size: 28px;
		color: #595959;
		cursor: pointer;

		&:disabled {
			cursor: not-allowed;
		}
	}

	&__reply-list {
		list-style: none;
		margin: 16px 0 0;
		padding: 0 0 0 20px;
		border-left: 2px solid #f0f0f0;
	}

	&__reply-item {
		display: flex;
		gap: 16px;
		margin-bottom: 24px;

		&:last-child {
			margin-bottom: 0;
		}
	}

	&__reply-avatar {
		width: 48px;
		height: 48px;
		border-radius: 50%;
		object-fit: cover;
		flex-shrink: 0;
	}

	&__reply-body {
		flex: 1;
		min-width: 0;
	}

	&__load-more-wrap {
		list-style: none;
		margin-top: 12px;
	}

	&__empty {
		margin: 0;
		padding: 80px 0;
		text-align: center;
		font-size: 28px;
		color: #999999;
	}

	&__load-more {
		display: block;
		width: 100%;
		margin-top: 24px;
		padding: 16px 0;
		border: none;
		background: transparent;
		font-size: 26px;
		color: #666666;
		cursor: pointer;

		&:disabled {
			cursor: not-allowed;
			color: #bbbbbb;
		}
	}

	&__input-bar {
		padding: 16px 30px 24px;
		flex-shrink: 0;
		border-top: 1px solid #f0f0f0;
	}

	&__input-wrap {
		display: flex;
		align-items: center;
		gap: 16px;
		height: 80px;
		padding: 0 12px 0 24px;
		background: #f5f5f5;
		border-radius: 40px;

		input {
			flex: 1;
			min-width: 0;
			height: 100%;
			border: none;
			background: transparent;
			font-size: 28px;
			color: #333333;
			outline: none;

			&::placeholder {
				color: #bbbbbb;
			}

			&:disabled {
				cursor: not-allowed;
			}
		}
	}

	&__write-icon {
		flex-shrink: 0;
		width: 32px;
		height: 32px;
		object-fit: contain;
	}

	&__send-btn {
		flex-shrink: 0;
		width: 64px;
		height: 64px;
		border: none;
		background: #d9d9d9;
		border-radius: 50%;
		padding: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: background 0.2s ease;

		&:disabled {
			cursor: not-allowed;
			opacity: 0.6;
		}

		img {
			width: 100%;
			height: 100%;
			object-fit: contain;
		}
	}
}
</style>
